import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { TranslateModule } from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { PartialsModule } from '../../../partials/partials.module';
import { ModuleGuard } from '../../../../core/auth';
import {
  AffiliationEffects,
  affiliationsReducer,
  AffiliationsService,
  AffiliationTypeEffects,
  affiliationTypesReducer,
  AllergiesEffects,
  AllergiesReducer,
  AttachmentsService,
  ClientEffects,
  ClientService,
  clientsReducer,
  ConditionsEffects,
  ConditionsReducer,
  ContactEffects,
  ContactRelationshipEffects,
  contactRelationshipsReducer,
  contactRelationshipAgreementsReducer,
  ContactRelationshipsService,
  contactsReducer,
  ContactsService,
  DoctorEffects,
  DoctorService,
  doctorsReducer,
  DrugEffects,
  DrugGroupEffects,
  DrugGroupService,
  drugGroupsReducer,
  DrugService,
  drugsReducer,
  facilitiesReducer,
  FacilitiesService,
  FacilityEffects,
  InsuranceEffects,
  insurancesReducer,
  InsurancesService,
  NoteEffect,
  noteReducer,
  NoteService,
  NotificationEffects,
  notificationReducer,
  NotificationService,
  PatientEffects,
  PatientsImageChangeRequestEffects,
  PatientHeightEffects,
  patientHeightsReducer,
  PatientHeightsService,
  PatientInsuranceEffects,
  patientInsuranceReducer,
  PatientInsuranceService,
  patientsReducer,
  patientImageChangeRequestsReducer,
  PatientsService,
  PatientImageChangeRequestService,
  PatientWeightEffects,
  patientWeightsReducer,
  PatientWeightsService,
  pharmaciesReducer,
  PharmacyEffects,
  PharmacyGroupDrugEffects,
  PharmacyGroupDrugService,
  pharmacyGroupDrugsReducer,
  PharmacyGroupEffects,
  PharmacyGroupService,
  pharmacyGroupsReducer,
  PharmacyService,
  PractitionRoleEffects,
  PrescriptionEffects,
  PrescriptionService,
  prescriptionsReducer,
  TeamMemberEffects,
  TeamMemberService,
  teamMembersReducer,
  TemplateEffects,
  templatesReducer,
} from '../../../../core/lists';
import {
  HttpUtilsService,
  InterceptService,
  LayoutUtilsService,
  TypesUtilsService,
} from '../../../../core/_base/crud';
import {
  ActionNotificationComponent,
  ConfirmActionDialogComponent,
  DeleteEntityDialogComponent,
  FetchEntityDialogComponent,
  UpdateStatusDialogComponent,
} from '../../../partials/content/crud';
import { ListsComponent } from './lists.component';
import { ContactsListComponent } from './contacts/contacts-list/contacts-list.component';
import { ContactEditComponent } from './contacts/contact-edit/contact-edit.component';
import { RelationshipsListComponent } from './contacts/_subs/relationships/relationships-list/relationships-list.component';
import { RelationshipEditDialogComponent } from './contacts/_subs/relationships/relationship-edit/relationship-edit-dialog.component';
import { ConfirmEntityDetailsDialogComponent } from './contacts/_subs/relationships/relationship-edit/confirm-entity-details-dialog/confirm-entity-details-dialog';
import { RelationshipAgreementsDialogComponent } from './contacts/_subs/relationships/relationship-agreements/relationship-agreements-dialog.component';
import { DoctorsListComponent } from './doctors/doctors-list/doctors-list.component';
import { DoctorEditComponent } from './doctors/doctor-edit/doctor-edit.component';
import { PatientsListComponent } from './patients/patients-list/patients-list.component';
import { PatientEditComponent } from './patients/patient-edit/patient-edit.component';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { NgbModule, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsModule } from 'ngx-permissions';
import { FacilitiesListComponent } from './facilities/facilities-list/facilities-list.component';
import { FacilityEditComponent } from './facilities/facilities-edit/facility-edit.component';
import { AffiliationsListComponent } from './affiliations/components/affiliations-list/affiliations-list.component';
import { AffiliationEditComponent } from './affiliations/components/affiliation-edit/affiliation-edit.component';
import { PatientWeightComponent } from './patients/_subs/patient-weight/patient-weight.component';
import { PatientHeightComponent } from './patients/_subs/patient-height/patient-height.component';
import { NoteListComponent } from './notes/note-list/note-list.component';
import { AddNoteDialogComponent } from './notes/add-note-dialog/add-note-dialog.component';
import { MatChipsModule } from '@angular/material/chips';
import { TimelineService } from 'src/app/core/timeline/_services/timeline.service';
import { InlineSVGModule } from 'ng-inline-svg/lib_commonjs/inline-svg.module';
import { timelinesReducer } from 'src/app/core/timeline/_reducers/timeline.reducers';
import { TimelineEffects } from 'src/app/core/timeline/_effects/timeline.effects';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { PatientInsuranceComponent } from './patients/_subs/patient-insurance/patient-insurance.component';
import { AddInsuranceDialogComponent } from './patients/_subs/add-insurance-dialog/add-insurance-dialog.component';
import { TimelineModule } from '../timeline/timeline.module';
import { PatientAffiliationComponent } from './patients/_subs/patient-affiliation/patient-affiliation.component';
import { PractitionRolesListComponent } from './doctors/practition-role/practition-role-list.component';
import { practitionRolesReducer } from 'src/app/core/lists/_reducers/practition-role.reducers';
import { PractitionRoleEditComponent } from './doctors/practition-role-edit/practition-role-edit.component';
import { AllergiesService } from '../../../../core/lists/_services/allergy.service';
import { AllergyEditComponent } from './patients/_subs/patient-allergy/allergy-edit/allergy-edit.component';
import { AllergyListComponent } from './patients/_subs/patient-allergy/allergy-list/allergy-list.component';
import { ConditionEditComponent } from './patients/_subs/patient-condition/condition-edit/condition-edit.component';
import { ConditionListComponent } from './patients/_subs/patient-condition/condition-list/condition-list.component';
import { ConditionsService } from '../../../../core/lists/_services/condition.service';
import { ConditionCategoryComponent } from './patients/_subs/condition-category/condition-category.component';
import { RoleViewDialogComponent } from './doctors/role-view/role-view-dialog.component';
import { DrugGroupsListComponent } from './drug-groups/drug-groups-list/drug-groups-list.component';
import { DrugGroupEditComponent } from './drug-groups/drug-group-edit/drug-group-edit.component';
import { DrugsListComponent } from './drugs/drugs-list/drugs-list.component';
import { DrugEditComponent } from './drugs/drug-edit/drug-edit.component';
import { PharmacyGroupsListComponent } from './pharmacy-groups/pharmacy-groups-list/pharmacy-groups-list.component';
import { PharmacyGroupEditComponent } from './pharmacy-groups/pharmacy-group-edit/pharmacy-group-edit.component';
import { PharmacyGroupDrugEditDialogComponent } from './pharmacy-groups/_subs/drugs/drug-edit/drug-edit-dialog.component';
import { PharmacyGroupDrugsListComponent } from './pharmacy-groups/_subs/drugs/drugs-list/drugs-list.component';
import { PharmaciesListComponent } from './pharmacies/pharmacies-list/pharmacies-list.component';
import { PharmacyEditComponent } from './pharmacies/pharmacy-edit/pharmacy-edit.component';
import { ClientsListComponent } from './clients/clients-list/clients-list.component';
import { ClientEditComponent } from './clients/client-edit/client-edit.component';
import { PrescriptionEditComponent } from './patients/_subs/prescription-edit/prescription-edit.component';
import { PrescriptionsListComponent } from './patients/_subs/prescriptions-list/prescriptions-list.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { InsuranceEditComponent } from './insurances/insurance-edit/insurance-edit.component';
import { InsurancesListComponent } from './insurances/insurances-list/insurances-list.component';
import { ImmunizationEditComponent } from './patients/_subs/patient-immunization/immunization-edit/immunization-edit.component';
import { ImmunizationListComponent } from './patients/_subs/patient-immunization/immunization-list/immunization-list.component';
import { PatientDevicesListComponent } from './patients/_subs/patient-devices/patient-devices-list/patient-devices-list.component';
import { MatFormioModule } from 'angular-material-formio';
// tslint:disable-next-line:max-line-length
import { PatientDeviceViewDialogComponent } from './patients/_subs/patient-devices/patient-device-view/patient-device-view.dialog.component';
// tslint:disable-next-line:max-line-length
import { PatientVitalsViewDialogComponent } from './patients/_subs/patient-devices/patient-vitals-view/patient-vitals-view.dialog.component';
import { FormService } from 'src/app/core/events';
import { ImmunizationsReducer } from 'src/app/core/lists/_reducers/immunization.reducers';
import { ImmunizationsEffects } from 'src/app/core/lists/_effects/immunization.effects';
import { ImmunizationsService } from 'src/app/core/lists/_services/immunization.service';
import { DoctorAffiliationComponent } from './doctors/doctor-affiliation/doctor-affiliation.component';
import { OfficesService } from '../../../../core/management';
import { AffiliationTypeListComponent } from './affiliations/components/affiliation-types-list/affiliation-types-list.component';
import { AffiliationTypeEditComponent } from './affiliations/components/affiliation-type-edit/affiliation-type-edit.component';
import { CreatePatientDialogComponent } from './patients/patient-add-dialog/patient-add.dialog.component';
import { EntityRelationshipEditComponent } from './doctors/relationships/relationship-edit/entity-relationship-edit.component';
import { EntityRelationshipsListComponent } from './doctors/relationships/relationships-list/entity-relationships-list.component';
import { CreateDoctorDialogComponent } from './doctors/doctor-add-dialog/doctor-add.dialog.component';
import { CreateContactDialogComponent } from '../../../partials/content/general/contact-add-dialog/contact-add.dialog.component';
import { CreateDrugDialogComponent } from './drugs/drug-add-dialog/drug-add.dialog.component';
import { CDK_DRAG_CONFIG, DragDropModule } from '@angular/cdk/drag-drop';
import { TagsListComponent } from './tags-list/tags-list.component';
import { AdminAddTagsDialogComponent } from './tags-list/admin-add-tags-dialog/admin-add-tags-dialog.component';
import { TagsListFilterComponent } from './tags-list/tags-list-filter/tags-list-filter.component';
import { CategoryAddDialogComponent } from './categories/category-add/category-add.dialog.component';
import { CategoriesListComponent } from './categories/categories-list/categories-list.component';
import { AssignDeviceComponent } from './patients/_subs/patient-devices/assign-device/assign-device.component';
import { PatientRangesComponent } from '../../me/device/patient-ranges/patient-ranges.component';
import { TemplateEditComponent } from './template/template-edit/template-edit.component';
import { TemplateDuplicateComponent } from './template/template-duplicate/template-duplicate.component';
import { TemplatesListComponent } from './template/template-list/templates-list.component';
import { TemplateService } from '../../../../core/lists/_services/template.service';
import { MergeContactsComponent } from './contacts/_subs/merge-contacts/merge-contacts.component';
import { GeneralValidationMessagesService } from '../../../../core/services/general-validation-messages.service';
import { FilteredViewInterceptor } from '../../../../core/auth/_helpers/filtered-view.interceptor';
import { AuthInterceptor } from '../../auth/auth.interceptor';
import { TaskModule } from '../../task/task.module';
import { PatientWorkflowListComponent } from './patients/_subs/workflow-list/workflow-list.component';
import { PatientWorkflowFilterComponent } from './patients/_subs/workflow-list/workflow-filter/workflow-filter.component';
import { PatientWorkflowAccordionComponent } from './patients/_subs/workflow-list/workflow-accordion/workflow-accordion.component';
import { PaymentInfoComponent } from './contacts/_subs/payment-info/payment-info.component';
import { FollowUpDialogComponent } from '../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { NewAttemptDialogComponent } from '../../follow-up/new-attempt-dialog/new-attempt-dialog.component';
import { ContactHitoryTabComponent } from './contacts/contact-hitory-tab/contact-hitory-tab.component';
import { PatientModReviewComponent } from './patients/patient-mod-review/patient-mod-review.component';
import { PatientHistoryComponent } from './patients/_subs/patient-history/patient-history.component';
import { AttemptListComponent } from '../../follow-up/attempt-list/attempt-list.component';
// import {NotificationListComponent} from './notifications/notification-list/notification-list.component';
import { CreateNotificationComponent } from './notifications/create-notification/create-notification.component';
import { ContactLoginComponent } from './contacts/_subs/contact-login/contact-login.component';
import { DeviceHistoryComponent } from './contacts/_subs/contact-login/device-history/device-history.component';
import { ContactCredentialComponent } from './contacts/_subs/contact-login/contact-credential/contact-credential.component';
import { ResetContactCredentialComponent } from './contacts/_subs/contact-login/reset-contact-credential/reset-contact-credential.component';
import { DrpComponent } from '../../med-review/drp/drp.component';
import { InterventionHistoryListComponent } from '../../med-review/drp/intervention-history-list/intervetion-history-list.component';
import { PendProblemComponent } from '../../med-review/drp/pend-problem/pend-problem.component';
import { ManageProblemComponent } from '../../med-review/drp/manage-problem/manage-problem.component';
import { InterventionHistoryEffects } from '../../med-review/effects/intervention-history.effects';
import { interventionHistoryReducer } from '../../med-review/reducers/intervention-history.reducer';
import { CustomDrpComponent } from '../../med-review/drp/custom-drp/custom-drp.component';
import { ReasonsListComponent } from './reasons/reasons-list/reasons-list.component';
import { ReasonEditComponent } from './reasons/reason-edit/reason-edit.component';
import { reasonsReducer } from '../../../../core/lists/_reducers/reason.reducers';
import { ReasonEffects } from '../../../../core/lists/_effects/reason.effects';
import { ReasonService } from '../../../../core/lists/_services/reasons.service';
import { CoreModule } from '../../../../core/core.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { MedReviewModule } from '../../med-review/med-review.module';
import { TeamMembersListComponent } from './categories/team-members-list/team-members-list.component';
import { EntityDetailsOffcanvasComponent } from './entity-details-offcanvas/entity-details-offcanvas.component';
import { CategoryDescriptionEditComponent } from './category description/category-description-edit/category-description-edit.component';
import { CategoryDescriptionListComponent } from './category description/category-description-list/category-description-list.component';
import { RelationshipCategoryEditComponent } from './relationship category/relationship-category-edit/relationship-category-edit.component';
import { RelationshipCategoryListComponent } from './relationship category/relationship-category-list/relationship-category-list.component';
import { RelationshipCategoryService } from '../../../../core/lists/_services/relationship-category.service';
import { CategoryDescriptionService } from '../../../../core/lists/_services/category-description.service';
import { RelationshipCategoryEffects } from '../../../../core/lists/_effects/relationship-category.effects';
import { CategoryDescriptionEffects } from '../../../../core/lists/_effects/category-description.effects';
import { RelationshipCategoryReducer } from '../../../../core/lists/_reducers/relationship-category.reducers';
import { CategoryDescriptionReducer } from '../../../../core/lists/_reducers/category-description.reducers';
import { FollowupReasonsListComponent } from './followup-reasons/followup-reasons-list/followup-reasons-list.component';
import { FollowupReasonEditComponent } from './followup-reasons/followup-reason-edit/followup-reason-edit.component';
import { followupReasonsReducer } from '../../../../core/lists/_reducers/followup-reason.reducers';
import { FollowupReasonEffects } from '../../../../core/lists/_effects/followup-reason.effects';
import { FollowupReasonsService } from '../../../../core/lists/_services/followup-reasons.service';
import { AffiliationRelationListComponent } from './affiliations/components/affiliation-relation-list/affiliation-relation-list.component';
import { affiliationRelationReducer } from './affiliations/reducers';
import { AffiliationRelationEffects } from './affiliations/effects';
import { AffiliationRelationService } from './affiliations/services';
import { AgmCoreModule } from '@agm/core';
import { SpecialtyService } from '../../../../core/lists/_services/specialty.service';
import { MaskPipe, NgxMaskModule } from 'ngx-mask';
import { ContactLoginHistoryComponent } from './contacts/_subs/contact-login/contact-login-history/contact-login-history.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { ExternalAccessEditComponent } from './external access/external-access-edit/external-access-edit..component';
import { InternalAccessListComponent } from './internal access/internal-access-list/internal-access-list.component';
import { PageGroupListComponent } from './page group/page-group-list/page-group-list.component';
import { InternalAccessEditComponent } from './internal access/internal-access-edit/internal-access-edit..component';
import { PageGroupEditComponent } from './page group/page-group-edit/page-group-edit..component';
import { ExternalAccessListComponent } from './external access/external-access-list/external-access-list.component';
import { ApplicationQrveyListComponent } from './application-qrvey/application-qrvey-list/application-qrvey-list.component';
import { ApplicationQrveyEditComponent } from './application-qrvey/application-qrvey-edit/application-qrvey-edit..component';
import { QrveyService } from 'src/app/core/lists/_services/qrvey.service';
import { UsersAccessListComponent } from './users-access-list/users-access-list.component';
import { QrveyDeployePageslistComponent } from './qrvey-deploye-pages-list/qrvey-deploye-pages-list.component';
import { contactsLoginHistoryReducer } from '../../../../core/lists/_reducers/contacts-login-history.reducers';
import { ContactLoginHistoryEffects } from '../../../../core/lists/_effects/contact-login-history.effects';
import { ZoomInEntityComponent } from '../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { StartFocusedViewFromEntityComponent } from '../../../partials/content/general/start-focused-view-from-entity/start-focused-view-from-entity.component';
import { DocumentsManagementModule } from '../../documents-management/documents-management.module';
import { PictureCropperComponent } from '../../../partials/content/general/picture-cropper/picture-cropper.component';
import { AdditionalStepsComponent } from './template/additional-steps/additional-steps.component';
import { AdditionalStepsService } from '../../../../core/services/additional-steps.service';
import { ChecklistTemplateComponent } from './template/checklist-template/checklist-template.component';
import { ChecklistTemplateService } from '../../../../core/services/checklist-template.service';
import { TaskActionsTabComponent } from './template/task-actions-tab/task-actions-tab.component';
import { TaskTemplateAiTabComponent } from './template/task-template-ai-tab/task-template-ai-tab.component';
import { TaskActionsService } from '../../../../core/services/task-actions.service';
import { ActionsListComponent } from './template/action-list/actions-list.component';
import { actionsReducer } from '../../../../core/lists/_reducers/action.reducers';
import { ActionEffects } from '../../../../core/lists/_effects/action.effects';
import { ActionService } from '../../../../core/lists/_services/action.service';
import { ActionEditComponent } from './template/action-edit/action-edit.component';
import { QuillModule } from 'ngx-quill';
import { CustomMappingEditorComponent } from './template/task-actions-tab/custom-mapping-editor/custom-mapping-editor.component';
import { TemplateBehaviorComponent } from './template/template-behavior/template-behavior.component';
import { RelatedTemplatesComponent } from './template/related-templates/related-templates.component';
import { RelatedTemplatesService } from '../../../../core/services/related-templates.service';
import { PrescriptionNoteComponent } from './patients/_subs/prescription-note/prescription-note.component';
import { UserManagementModule } from '../../user-management/user-management.module';
import { AgreementManagementModule } from '../../agreement-management/agreement-management.module';
import { ContactRelationshipAgreementEffects } from 'src/app/core/lists/_effects/contact-relationship-agreements.effects';
import { AgreementTemplateEffects, agreementTemplatesReducer } from 'src/app/core/agreement';
import { SharedModule } from 'src/app/shared/shared.module';
import { PatientsRequestsComponent } from './patients/patients-requests/patients-requests.component';
import { ConfirmDenialComponent } from './patients/patients-requests/confirm-denial-dialog/confirm-denial-dialog.component';
import { PatientRequestReviewComponent } from './patients/patient-request-review/patient-request-review.component';
import { BridgePayModule } from 'src/app/core/bridge-pay/bridgepay.module';
import { ContactDuplicateCellNumberComponent } from './contacts/contact-duplicate-cell-number/contact-duplicate-cell-number.component';
import { ContactListEffects } from './contacts/contact-duplicate-cell-number/store/effects';
import { contactListReducer } from './contacts/contact-duplicate-cell-number/store/reducers';
import { FormsTemplateService } from '../../forms-templates/services';
import { TagEffects } from '../../../../core/lists/_effects/tag.effects';
import { tagsReducer } from '../../../../core/lists/_reducers/tag.reducers';
import { TagService } from '../../../../core/lists/_services/tags.service';
import { applicationQrveyReducer } from '../../../../core/lists/_reducers/application-qrvey.reducers';
import { ApplicationQrveyEffects } from '../../../../core/lists/_effects/application-qrvey.effects';
import { EntitiesDialogComponent } from './template/entities-dialog/entities-dialog.component';

const routes: Routes = [
  {
    path: '',
    component: ListsComponent,
    children: [
      {
        path: '',
        redirectTo: 'contacts',
        pathMatch: 'full',
      },
      {
        path: 'doctors',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToDoctorModule' },
        component: DoctorsListComponent,
      },
      {
        path: 'doctors/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddDoctor' },
        component: DoctorEditComponent,
      },
      {
        path: 'doctors/edit',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToDoctorModule' },
        component: DoctorEditComponent,
      },
      {
        path: 'doctors/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToDoctorModule' },
        component: DoctorEditComponent,
      },
      {
        path: 'doctors/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewDoctor' },
        component: DoctorEditComponent,
      },
      {
        path: 'doctors/:id/practitioner-role/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPractitionerModule' },
        component: PractitionRoleEditComponent,
      },
      {
        path: 'doctors/:id/practitioner-role/edit/:pra_id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPractitionerModule' },
        component: PractitionRoleEditComponent,
      },
      {
        path: 'facilities',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToFacilityModule' },
        component: FacilitiesListComponent,
      },
      {
        path: 'facilities/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToFacilityModule' },
        component: FacilityEditComponent,
      },
      {
        path: 'facilities/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewFacility' },
        component: FacilityEditComponent,
      },
      {
        path: 'facilities/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddFacility' },
        component: FacilityEditComponent,
      },
      {
        path: 'affiliations',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToAffiliationModule' },
        component: AffiliationsListComponent,
      },
      {
        path: 'affiliations/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddAffiliation' },
        component: AffiliationEditComponent,
      },
      {
        path: 'affiliations/edit',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditAffiliation' },
        component: AffiliationEditComponent,
      },
      {
        path: 'affiliations/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditAffiliation' },
        component: AffiliationEditComponent,
      },
      {
        path: 'affiliations/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewAffiliation' },
        component: AffiliationEditComponent,
      },
      {
        path: 'patients',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPatientModule' },
        component: PatientsListComponent,
      },
      {
        path: 'patients/requests',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPatientImageChangeRequests' },
        component: PatientsRequestsComponent,
      },
      {
        path: 'patients/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPatientModule' },
        component: PatientEditComponent,
      },
      {
        path: 'patients/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewPatient' },
        component: PatientEditComponent,
      },
      {
        path: 'patients/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddPatient' },
        component: PatientEditComponent,
      },
      {
        path: 'contacts',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToContactModule' },
        component: ContactsListComponent,
      },
      {
        path: 'inbound',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToContactModule' },
        component: ContactsListComponent,
      },
      {
        path: 'contacts/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddContact' },
        component: ContactEditComponent,
      },
      {
        path: 'contacts/edit',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToContactModule' },
        component: ContactEditComponent,
      },
      {
        path: 'contacts/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToContactModule' },
        component: ContactEditComponent,
      },
      {
        path: 'contacts/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewContact' },
        component: ContactEditComponent,
      },
      {
        path: 'drug-groups',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToDrugGroupModule' },
        component: DrugGroupsListComponent,
      },
      {
        path: 'drug-groups/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddDrugGroup' },
        component: DrugGroupEditComponent,
      },
      {
        path: 'drug-groups/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditDrugGroup' },
        component: DrugGroupEditComponent,
      },
      {
        path: 'drug-groups/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewDrugGroup' },
        component: DrugGroupEditComponent,
      },
      {
        path: 'drugs',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToDrugModule' },
        component: DrugsListComponent,
      },
      {
        path: 'drugs/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddDrug' },
        component: DrugEditComponent,
      },
      {
        path: 'drugs/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditDrug' },
        component: DrugEditComponent,
      },
      {
        path: 'drugs/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewDrug' },
        component: DrugEditComponent,
      },
      {
        path: 'pharmacy-groups',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPharmacyGroupModule' },
        component: PharmacyGroupsListComponent,
      },
      {
        path: 'pharmacy-groups/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddPharmacyGroup' },
        component: PharmacyGroupEditComponent,
      },
      {
        path: 'pharmacy-groups/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditPharmacyGroup' },
        component: PharmacyGroupEditComponent,
      },
      {
        path: 'pharmacy-groups/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewPharmacyGroup' },
        component: PharmacyGroupEditComponent,
      },
      {
        path: 'pharmacies',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToPharmacyModule' },
        component: PharmaciesListComponent,
      },
      {
        path: 'pharmacies/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddPharmacy' },
        component: PharmacyEditComponent,
      },
      {
        path: 'pharmacies/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditPharmacy' },
        component: PharmacyEditComponent,
      },
      {
        path: 'pharmacies/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewPharmacy' },
        component: PharmacyEditComponent,
      },
      {
        path: 'clients',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToClientModule' },
        component: ClientsListComponent,
      },
      {
        path: 'clients/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddClient' },
        component: ClientEditComponent,
      },
      {
        path: 'clients/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditClient' },
        component: ClientEditComponent,
      },
      {
        path: 'clients/view/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canViewClient' },
        component: ClientEditComponent,
      },
      {
        path: 'insurances',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToInsuranceModule' },
        component: InsurancesListComponent,
      },
      {
        path: 'reasons',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToInsuranceModule' },
        component: ReasonsListComponent,
      },
      {
        path: 'FollowupReasons',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToFollowupReasonModule' },
        component: FollowupReasonsListComponent,
      },
      {
        path: 'RelationshipCategory',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToRelationshipCategoryModule' },
        component: RelationshipCategoryListComponent,
      },
      {
        path: 'CategoryDescription',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToCategoryDescriptionModule' },
        component: CategoryDescriptionListComponent,
      },
      {
        path: 'insurances/add',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canAddInsurance' },
        component: InsuranceEditComponent,
      },
      {
        path: 'insurances/edit',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditInsurance' },
        component: InsuranceEditComponent,
      },
      {
        path: 'insurances/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'canEditInsurance' },
        component: InsuranceEditComponent,
      },
      {
        path: 'affiliationTypes',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToAffiliationTypeModule' },
        component: AffiliationTypeListComponent,
      },
      {
        path: 'tags',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToTagsModule' },
        component: TagsListComponent,
      },
      {
        path: 'team',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToTeamModule' },
        component: CategoriesListComponent,
      },
      {
        path: 'template',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToTemplateModule' },
        component: TemplatesListComponent,
      },
      {
        path: 'actions',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessToTemplateModule' },
        component: ActionsListComponent,
      },
      // {
      //   path: 'notifications',
      //   canActivate: [ModuleGuard],
      //   data: {permissionName: 'accessManageNotifications'},
      //   component: NotificationListComponent,
      // },
      {
        path: 'ExternalAccessList',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessAnalyticsAccessList' },
        component: ExternalAccessListComponent,
      },
      {
        path: 'InternalAccessList',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessAnalyticsAccessList' },
        component: InternalAccessListComponent,
      },
      {
        path: 'applications',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessAnalyticsAccessList' },
        component: ApplicationQrveyListComponent,
      },
      {
        path: 'applications/edit/:id',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessAnalyticsAccessList' },
        component: ApplicationQrveyEditComponent,
      },
      {
        path: 'PageGroupListComponent',
        canActivate: [ModuleGuard],
        data: { permissionName: 'accessAnalyticsAccessList' },
        component: PageGroupListComponent,
      },
    ],
  },
];

const DragConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 10000,
};

@NgModule({
  imports: [
    TimelineModule,
    MatButtonToggleModule,
    NgbModule,
    MatChipsModule,
    PerfectScrollbarModule,
    MatDialogModule,
    InlineSVGModule,
    CommonModule,
    HttpClientModule,
    PartialsModule,
    NgxPermissionsModule.forChild(),
    RouterModule.forChild(routes),
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forChild(),
    MatButtonModule,
    MatMenuModule,
    MatSelectModule,
    MatInputModule,
    MatTableModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatIconModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatCardModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTabsModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatListModule,
    NgbProgressbarModule,
    SharedModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    MatFormioModule,
    DragDropModule,
    TaskModule,
    StoreModule.forFeature('contacts', contactsReducer),
    EffectsModule.forFeature([ContactEffects]),
    StoreModule.forFeature('agreementTemplates', agreementTemplatesReducer),
    EffectsModule.forFeature([AgreementTemplateEffects]),
    StoreModule.forFeature('contactRelationships', contactRelationshipsReducer),
    EffectsModule.forFeature([ContactRelationshipEffects]),
    StoreModule.forFeature('contactRelationshipAgreements', contactRelationshipAgreementsReducer),
    EffectsModule.forFeature([ContactRelationshipAgreementEffects]),
    StoreModule.forFeature('contactsList', contactListReducer),
    EffectsModule.forFeature([ContactListEffects]),
    StoreModule.forFeature('doctors', doctorsReducer),
    EffectsModule.forFeature([DoctorEffects]),
    StoreModule.forFeature('facilities', facilitiesReducer),
    EffectsModule.forFeature([FacilityEffects]),
    StoreModule.forFeature('patients', patientsReducer),
    EffectsModule.forFeature([PatientEffects]),
    StoreModule.forFeature('requests', patientImageChangeRequestsReducer),
    EffectsModule.forFeature([PatientsImageChangeRequestEffects]),
    StoreModule.forFeature('timelines', timelinesReducer),
    EffectsModule.forFeature([TimelineEffects]),
    StoreModule.forFeature('affiliations', affiliationsReducer),
    EffectsModule.forFeature([AffiliationEffects]),
    StoreModule.forFeature('affiliationTypes', affiliationTypesReducer),
    EffectsModule.forFeature([AffiliationTypeEffects]),
    StoreModule.forFeature('insurances', insurancesReducer),
    EffectsModule.forFeature([InsuranceEffects]),
    StoreModule.forFeature('patientWeights', patientWeightsReducer),
    EffectsModule.forFeature([PatientWeightEffects]),
    StoreModule.forFeature('patientHeights', patientHeightsReducer),
    EffectsModule.forFeature([PatientHeightEffects]),
    StoreModule.forFeature('note', noteReducer),
    EffectsModule.forFeature([NoteEffect]),
    StoreModule.forFeature('patientInsurance', patientInsuranceReducer),
    EffectsModule.forFeature([PatientInsuranceEffects]),
    StoreModule.forFeature('practitionerRoles', practitionRolesReducer),
    EffectsModule.forFeature([PractitionRoleEffects]),
    StoreModule.forFeature('allergies', AllergiesReducer),
    EffectsModule.forFeature([AllergiesEffects]),
    StoreModule.forFeature('immunizations', ImmunizationsReducer),
    EffectsModule.forFeature([ImmunizationsEffects]),
    StoreModule.forFeature('conditions', ConditionsReducer),
    EffectsModule.forFeature([ConditionsEffects]),
    StoreModule.forFeature('drugGroups', drugGroupsReducer),
    EffectsModule.forFeature([DrugGroupEffects]),
    StoreModule.forFeature('applicationQrvey', applicationQrveyReducer),
    EffectsModule.forFeature([ApplicationQrveyEffects]),
    StoreModule.forFeature('drugs', drugsReducer),
    EffectsModule.forFeature([DrugEffects]),
    StoreModule.forFeature('reasons', reasonsReducer),
    EffectsModule.forFeature([TagEffects]),
    StoreModule.forFeature('tags', tagsReducer),
    EffectsModule.forFeature([ReasonEffects]),
    StoreModule.forFeature('RelationshipCategory', RelationshipCategoryReducer),
    EffectsModule.forFeature([RelationshipCategoryEffects]),
    StoreModule.forFeature('CategoryDescription', CategoryDescriptionReducer),
    EffectsModule.forFeature([CategoryDescriptionEffects]),
    StoreModule.forFeature('client', clientsReducer),
    EffectsModule.forFeature([ClientEffects]),
    StoreModule.forFeature('pharmacies', pharmaciesReducer),
    EffectsModule.forFeature([PharmacyEffects]),
    StoreModule.forFeature('pharmacyGroups', pharmacyGroupsReducer),
    EffectsModule.forFeature([PharmacyGroupEffects]),
    StoreModule.forFeature('pharmacyGroupDrugs', pharmacyGroupDrugsReducer),
    EffectsModule.forFeature([PharmacyGroupDrugEffects]),
    StoreModule.forFeature('prescriptions', prescriptionsReducer),
    EffectsModule.forFeature([PrescriptionEffects]),
    StoreModule.forFeature('templates', templatesReducer),
    EffectsModule.forFeature([TemplateEffects]),
    StoreModule.forFeature('actions', actionsReducer),
    EffectsModule.forFeature([ActionEffects]),
    StoreModule.forFeature('notifications', notificationReducer),
    EffectsModule.forFeature([NotificationEffects]),
    StoreModule.forFeature('interventionHistory', interventionHistoryReducer),
    EffectsModule.forFeature([InterventionHistoryEffects]),
    StoreModule.forFeature('teamMembers', teamMembersReducer),
    EffectsModule.forFeature([TeamMemberEffects]),
    StoreModule.forFeature('FollowupReasons', followupReasonsReducer),
    EffectsModule.forFeature([FollowupReasonEffects]),
    StoreModule.forFeature('affiliationRelation', affiliationRelationReducer),
    EffectsModule.forFeature([AffiliationRelationEffects]),
    StoreModule.forFeature('FollowupReasons', followupReasonsReducer),
    EffectsModule.forFeature([FollowupReasonEffects]),
    StoreModule.forFeature('contactsLoginHistory', contactsLoginHistoryReducer),
    EffectsModule.forFeature([ContactLoginHistoryEffects]),
    CoreModule,
    TooltipModule.forRoot(),
    MedReviewModule,
    AgmCoreModule,
    NgxMaskModule,
    ImageCropperModule,
    ImageCropperModule,
    UserManagementModule,
    AgreementManagementModule,
    DocumentsManagementModule,
    QuillModule,
    BridgePayModule,
  ],
  providers: [
    TeamMemberService,
    GeneralValidationMessagesService,
    PatientInsuranceService,
    ModuleGuard,
    InterceptService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FilteredViewInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        panelClass: 'mat-dialog-container-wrapper',
        height: 'auto',
        width: '900px',
      },
    },
    { provide: CDK_DRAG_CONFIG, useValue: DragConfig },
    TypesUtilsService,
    LayoutUtilsService,
    HttpUtilsService,
    ContactsService,
    TimelineService,
    ContactRelationshipsService,
    NoteService,
    AllergiesService,
    ImmunizationsService,
    ConditionsService,
    DoctorService,
    FacilitiesService,
    PatientsService,
    PatientImageChangeRequestService,
    AffiliationsService,
    InsurancesService,
    TypesUtilsService,
    LayoutUtilsService,
    DatePipe,
    PatientWeightsService,
    PatientHeightsService,
    AttachmentsService,
    DrugGroupService,
    ClientService,
    DrugService,
    PharmacyService,
    PharmacyGroupService,
    PharmacyGroupDrugService,
    PrescriptionService,
    TemplateService,
    FormsTemplateService,
    FormService,
    OfficesService,
    NotificationService,
    ReasonService,
    TagService,
    FollowupReasonsService,
    RelationshipCategoryService,
    CategoryDescriptionService,
    AffiliationRelationService,
    QrveyService,
    SpecialtyService,
    MaskPipe,
    AdditionalStepsService,
    RelatedTemplatesService,
    ChecklistTemplateService,
    TaskActionsService,
    ActionService,
  ],
  entryComponents: [
    QrveyDeployePageslistComponent,
    UsersAccessListComponent,
    ZoomInEntityComponent,
    PictureCropperComponent,
    TeamMembersListComponent,
    ResetContactCredentialComponent,
    FollowUpDialogComponent,
    NewAttemptDialogComponent,
    AttemptListComponent,
    MergeContactsComponent,
    PatientRangesComponent,
    AdminAddTagsDialogComponent,
    ActionNotificationComponent,
    DoctorEditComponent,
    ConfirmActionDialogComponent,
    DeleteEntityDialogComponent,
    FetchEntityDialogComponent,
    UpdateStatusDialogComponent,
    RelationshipEditDialogComponent,
    ConfirmEntityDetailsDialogComponent,
    RelationshipAgreementsDialogComponent,
    EntityRelationshipEditComponent,
    FacilityEditComponent,
    PatientEditComponent,
    AffiliationEditComponent,
    AffiliationTypeEditComponent,
    InsuranceEditComponent,
    PatientWeightComponent,
    PatientAffiliationComponent,
    DoctorAffiliationComponent,
    ConditionCategoryComponent,
    PatientHeightComponent,
    AddNoteDialogComponent,
    AllergyEditComponent,
    ImmunizationEditComponent,
    RoleViewDialogComponent,
    ConditionEditComponent,
    PatientHeightComponent,
    AddInsuranceDialogComponent,
    PharmacyGroupDrugEditDialogComponent,
    PrescriptionEditComponent,
    TemplateEditComponent,
    TemplateDuplicateComponent,
    TemplateBehaviorComponent,
    CustomMappingEditorComponent,
    ActionEditComponent,
    PatientDeviceViewDialogComponent,
    PatientVitalsViewDialogComponent,
    CreatePatientDialogComponent,
    CreateDoctorDialogComponent,
    CreateContactDialogComponent,
    CreateDrugDialogComponent,
    CategoryAddDialogComponent,
    AssignDeviceComponent,
    CreateNotificationComponent,
    ReasonEditComponent,
    FollowupReasonEditComponent,
    CategoryDescriptionEditComponent,
    RelationshipCategoryEditComponent,
    DrpComponent,
    InterventionHistoryListComponent,
    PendProblemComponent,
    ManageProblemComponent,
    CustomDrpComponent,
    PageGroupEditComponent,
    ExternalAccessEditComponent,
    ApplicationQrveyEditComponent,
    InternalAccessEditComponent,
    StartFocusedViewFromEntityComponent,
    PrescriptionNoteComponent,
    ConfirmDenialComponent,
    PatientRequestReviewComponent,
    ContactDuplicateCellNumberComponent,
    EntitiesDialogComponent
  ],
  declarations: [
    QrveyDeployePageslistComponent,
    ZoomInEntityComponent,
    PictureCropperComponent,
    UsersAccessListComponent,
    TeamMembersListComponent,
    DeviceHistoryComponent,
    ContactCredentialComponent,
    ContactLoginComponent,
    ListsComponent,
    ContactsListComponent,
    RelationshipsListComponent,
    EntityRelationshipsListComponent,
    DoctorsListComponent,
    PractitionRolesListComponent,
    AffiliationsListComponent,
    InsurancesListComponent,
    ContactEditComponent,
    DoctorEditComponent,
    PractitionRoleEditComponent,
    PatientAffiliationComponent,
    DoctorAffiliationComponent,
    ConditionCategoryComponent,
    RelationshipEditDialogComponent,
    ConfirmEntityDetailsDialogComponent,
    RelationshipAgreementsDialogComponent,
    EntityRelationshipEditComponent,
    FacilitiesListComponent,
    EntityDetailsOffcanvasComponent,
    FacilityEditComponent,
    AffiliationEditComponent,
    InsuranceEditComponent,
    PatientsListComponent,
    PatientsRequestsComponent,
    PatientEditComponent,
    PatientWeightComponent,
    PatientHeightComponent,
    PatientWorkflowListComponent,
    PatientWorkflowFilterComponent,
    PatientWorkflowAccordionComponent,
    NoteListComponent,
    AddNoteDialogComponent,
    AllergyEditComponent,
    AffiliationTypeEditComponent,
    ImmunizationEditComponent,
    RoleViewDialogComponent,
    AllergyListComponent,
    AffiliationTypeListComponent,
    ImmunizationListComponent,
    ConditionEditComponent,
    ConditionListComponent,
    PatientInsuranceComponent,
    AddInsuranceDialogComponent,
    DrugGroupsListComponent,
    DrugGroupEditComponent,
    DrugsListComponent,
    DrugEditComponent,
    ClientsListComponent,
    ClientEditComponent,
    PharmaciesListComponent,
    PharmacyEditComponent,
    PharmacyGroupEditComponent,
    PharmacyGroupsListComponent,
    PharmacyGroupDrugsListComponent,
    PharmacyGroupDrugEditDialogComponent,
    PrescriptionsListComponent,
    PrescriptionEditComponent,
    TemplatesListComponent,
    ActionsListComponent,
    TemplateEditComponent,
    TemplateDuplicateComponent,
    TemplateBehaviorComponent,
    CustomMappingEditorComponent,
    ActionEditComponent,
    AdditionalStepsComponent,
    RelatedTemplatesComponent,
    ChecklistTemplateComponent,
    TaskActionsTabComponent,
    TaskTemplateAiTabComponent,
    PatientDevicesListComponent,
    PatientDeviceViewDialogComponent,
    PatientVitalsViewDialogComponent,
    CreatePatientDialogComponent,
    CreateDoctorDialogComponent,
    CreateDrugDialogComponent,
    TagsListComponent,
    AdminAddTagsDialogComponent,
    TagsListFilterComponent,
    CategoriesListComponent,
    CategoryAddDialogComponent,
    AssignDeviceComponent,
    MergeContactsComponent,
    PaymentInfoComponent,
    ContactHitoryTabComponent,
    PatientModReviewComponent,
    PatientHistoryComponent,
    // NotificationListComponent,
    CreateNotificationComponent,
    ReasonsListComponent,
    FollowupReasonsListComponent,
    ReasonEditComponent,
    FollowupReasonEditComponent,
    CategoryDescriptionEditComponent,
    CategoryDescriptionListComponent,
    RelationshipCategoryEditComponent,
    RelationshipCategoryListComponent,
    ResetContactCredentialComponent,
    AffiliationRelationListComponent,
    ContactLoginHistoryComponent,
    PageGroupListComponent,
    InternalAccessListComponent,
    ApplicationQrveyListComponent,
    ApplicationQrveyEditComponent,
    ExternalAccessListComponent,
    PageGroupEditComponent,
    ExternalAccessEditComponent,
    InternalAccessEditComponent,
    StartFocusedViewFromEntityComponent,
    PrescriptionNoteComponent,
    ConfirmDenialComponent,
    PatientRequestReviewComponent,
    ContactDuplicateCellNumberComponent,
    EntitiesDialogComponent,
  ],
  exports: [
    PatientsListComponent,
    PrescriptionNoteComponent,
    ContactsListComponent,
    NoteListComponent,
    EntityDetailsOffcanvasComponent,
    ContactDuplicateCellNumberComponent,
  ],
})
export class ListsModule {}
